/**
 * Print Templates Dual-Write Test
 *
 * Tests that print template operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * PT1: Create print template (save new)
 * PT2: Update print template (save existing)
 * PT3: Delete print template
 */

import {
  savePrintTemplate,
  deletePrintTemplate,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';

// Test variation definitions
export const PRINT_TEMPLATES_TEST_VARIATIONS = [
  { id: 'PT1', category: 'create', name: 'Create print template' },
  { id: 'PT2', category: 'update', name: 'Update print template' },
  { id: 'PT3', category: 'delete', name: 'Delete print template' },
];

// Get test categories for UI grouping
export function getPrintTemplatesTestCategories() {
  return [
    { id: 'create', name: 'Create Template', variations: ['PT1'] },
    { id: 'update', name: 'Update Template', variations: ['PT2'] },
    { id: 'delete', name: 'Delete Template', variations: ['PT3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getPrintTemplatesTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  return {
    organization,
    store,
    db,
    organizationId,
  };
}

/**
 * Compare print template via backend API
 */
async function comparePrintTemplateViaApi(templateId, templateData, log) {
  try {
    const response = await api.get(`/print-templates/compare/${templateId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        templateId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'template',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare name
    if (templateData.name) {
      const sbName = normalizeValue(supabaseData?.name);
      const fsName = normalizeValue(templateData.name);
      if (!valuesMatch(sbName, fsName)) {
        differences.push({
          field: 'name',
          supabaseValue: sbName,
          firestoreValue: fsName,
          severity: 'critical',
        });
      }
    }

    // Compare type
    if (templateData.type) {
      const sbType = normalizeValue(supabaseData?.type);
      const fsType = normalizeValue(templateData.type);
      if (!valuesMatch(sbType, fsType)) {
        differences.push({
          field: 'type',
          supabaseValue: sbType,
          firestoreValue: fsType,
          severity: 'critical',
        });
      }
    }

    // Compare isDefault
    if (templateData.isDefault !== undefined) {
      const sbDefault = supabaseData?.is_default;
      const fsDefault = templateData.isDefault;
      if (sbDefault !== fsDefault) {
        differences.push({
          field: 'is_default',
          supabaseValue: sbDefault,
          firestoreValue: fsDefault,
          severity: 'warning',
        });
      }
    }

    return {
      templateId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      templateId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if template was deleted from Supabase
 */
async function verifyTemplateDeletedFromSupabase(templateId, log, maxRetries = 3) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await api.get(`/print-templates/compare/${templateId}`);
      const apiResult = response.data;

      if (!apiResult.exists) {
        return { deleted: true };
      }

      if (attempt < maxRetries) {
        log(`Template still exists in Supabase, waiting before retry ${attempt + 1}/${maxRetries}...`);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    } catch (error) {
      // 404 means it's deleted
      if (error.response?.status === 404) {
        return { deleted: true };
      }
      log(`Verification error: ${error.message}`);
    }
  }

  return { deleted: false };
}

/**
 * Delete a print template from Firestore (cleanup helper)
 */
async function deletePrintTemplateFromFirestore(db, templateId) {
  try {
    await db.collection('printTemplates').doc(templateId).delete();
  } catch (error) {
    console.warn('Failed to delete test print template:', error.message);
  }
}

/**
 * Run a single print template test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId } = testData;

  let createdTemplateId = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'PT1': {
        // Create print template
        const templateData = {
          name: `Test Template ${Date.now()}`,
          type: 'receipt',
          content: '<html><body>Test receipt template</body></html>',
          stores: [store.id],
          isDefault: false,
        };

        log('Creating print template...');
        const created = await savePrintTemplate(templateData);
        createdTemplateId = created.id;
        log(`Template created with ID: ${createdTemplateId}`);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await comparePrintTemplateViaApi(createdTemplateId, templateData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test template...');
        await deletePrintTemplate(createdTemplateId);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'PT2': {
        // Update print template test
        const templateData = {
          name: `Original Template ${Date.now()}`,
          type: 'receipt',
          content: '<html><body>Original content</body></html>',
          stores: [store.id],
          isDefault: false,
        };

        log('Creating template to update...');
        const created = await savePrintTemplate(templateData);
        createdTemplateId = created.id;

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await comparePrintTemplateViaApi(createdTemplateId, templateData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Template was not created in Supabase';
          break;
        }

        // Now update via Firestore
        const updatedData = {
          ...templateData,
          id: createdTemplateId,
          name: `Updated Template ${Date.now()}`,
          content: '<html><body>Updated content</body></html>',
          isDefault: true,
        };

        log('Updating template...');
        await savePrintTemplate(updatedData);

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await comparePrintTemplateViaApi(createdTemplateId, updatedData, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && updateCheck.matches;

        // Cleanup
        log('Cleaning up test template...');
        await deletePrintTemplate(createdTemplateId);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'PT3': {
        // Delete print template test
        const templateData = {
          name: `Template to delete ${Date.now()}`,
          type: 'label',
          content: '<html><body>To be deleted</body></html>',
          stores: [],
          isDefault: false,
        };

        log('Creating template to delete...');
        const created = await savePrintTemplate(templateData);
        createdTemplateId = created.id;

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await comparePrintTemplateViaApi(createdTemplateId, templateData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Template was not created in Supabase before delete test';
          break;
        }

        // Now delete via Firestore
        log('Deleting template via Firestore...');
        await deletePrintTemplate(createdTemplateId);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify deletion
        const deleteCheck = await verifyTemplateDeletedFromSupabase(createdTemplateId, log);
        result.passed = deleteCheck.deleted;
        result.comparisonResult = {
          templateId: createdTemplateId,
          foundInSupabase: !deleteCheck.deleted,
          matches: deleteCheck.deleted,
          differences: deleteCheck.deleted ? [] : [{
            field: 'deletion',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
          warnings: [],
        };

        // No cleanup needed - already deleted
        createdTemplateId = null;

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdTemplateId) {
      try {
        await deletePrintTemplateFromFirestore(db, createdTemplateId);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all print templates tests
 */
export async function runFullPrintTemplatesTest(log = console.log, selectedVariations = null) {
  log('Starting print templates dual-write tests...');

  const testData = getPrintTemplatesTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('PRINT TEMPLATES DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || PRINT_TEMPLATES_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  PRINT_TEMPLATES_TEST_VARIATIONS,
  getPrintTemplatesTestCategories,
  getPrintTemplatesTestData,
  runFullPrintTemplatesTest,
};
